// Verversen tegen Bouwflow (SPEC_workflow 7.3): wat er sinds de calculatie veranderde aan de opstartfiche, de
// opleveringsbon en de offertes, zodat de gebruiker met de meest recente informatie werkt voor hij iets naar
// de onderaannemer stuurt. De calculatie verandert pas als de gebruiker iets overneemt.
import { Link } from 'react-router-dom';
import { OPLEVERINGSBON_LABEL, type OpleveringsbonStatus } from '@recotex/shared';
import type { BouwflowControle } from '../lib/api';
import { fmtDate, fmtEUR, fmtNum } from '../lib/format';

export function BouwflowControleModal({ controle, bouwflowProjectId, opmerkingen, bezig, onOffertesToevoegen, onExtraInfoOvernemen, onSluit }: {
  controle: BouwflowControle;
  bouwflowProjectId: number;
  opmerkingen: string;
  bezig: boolean;
  onOffertesToevoegen: (offertes: BouwflowControle['extraOffertes']) => void;
  onExtraInfoOvernemen: (tekst: string) => void;
  onSluit: () => void;
}) {
  const f = controle.fiche;
  const o = controle.oplevering;
  const niets = controle.wijzigingen === 0;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="controle-titel">
      <div className="card max-w-2xl w-full max-h-full overflow-y-auto normal-case tracking-normal">
        <h2 id="controle-titel" className="text-xl font-bold mb-1">Vergeleken met Bouwflow</h2>
        <p className="text-sm text-secondary mb-4">
          {niets ? 'Niets gewijzigd sinds de calculatie: opstartfiche, opleveringsbon en offertes komen overeen.' : 'Dit veranderde sinds de calculatie gemaakt werd. Kijk het na voor je de calculatie naar de onderaannemer stuurt.'}
        </p>

        <section className="mb-4">
          <h3 className="text-base font-bold mb-1">Opstartfiche</h3>
          {!f.gevonden ? (
            <p className="text-sm">Geen opstartfiche gevonden onder dit project in Bouwflow.</p>
          ) : !f.gewijzigd ? (
            <p className="text-sm">Ongewijzigd{f.gewijzigdOp ? ` (laatst aangepast ${fmtDate(f.gewijzigdOp)})` : ''}.</p>
          ) : (
            <>
              <p className="text-sm mb-2">
                {f.andereFiche ? 'Er staat een andere, nieuwere opstartfiche in Bouwflow' : 'De opstartfiche werd aangepast'}
                {f.gewijzigdOp ? ` op ${fmtDate(f.gewijzigdOp)}` : ''}.
                {!f.posten.length && ' De posten en hoeveelheden zijn dezelfde.'}
              </p>
              {f.posten.length > 0 && (
                <table className="w-full text-sm mb-2">
                  <thead>
                    <tr className="thead-row"><th className="text-left px-2 py-1">Post</th><th className="text-right px-2 py-1">Op de calculatie</th><th className="text-right px-2 py-1">Nu op de fiche</th></tr>
                  </thead>
                  <tbody>
                    {f.posten.map((p) => (
                      <tr key={p.werken} className="border-b border-lightgrey">
                        <td className="px-2 py-1">{p.werken}</td>
                        <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap">{p.oud == null ? <span className="text-secondary">niet op de calculatie</span> : `${fmtNum(p.oud)} ${p.eenheid}`}</td>
                        <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap font-semibold">{p.nieuw == null ? <span className="text-danger">niet meer op de fiche</span> : `${fmtNum(p.nieuw)} ${p.eenheid}`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {f.posten.length > 0 && (
                <p className="text-xs text-secondary">
                  Pas de hoeveelheden aan via Bewerken, of maak een nieuwe calculatie uit de huidige fiche:{' '}
                  <Link className="underline font-semibold" to={`/project/new?bouwflow=${bouwflowProjectId}`}>Opstartfiche opnieuw ophalen</Link>.
                </p>
              )}
              {f.extraInformatieGewijzigd && (
                <div className="mt-3 p-2 rounded-md bg-lightgrey">
                  <p className="text-xs font-semibold mb-1">Extra informatie op de fiche</p>
                  <p className="text-sm whitespace-pre-wrap">{f.extraInformatie || <em>leeg</em>}</p>
                  {f.extraInformatie !== opmerkingen && (
                    <button type="button" className="btn-ghost btn-sm mt-2" disabled={bezig} onClick={() => onExtraInfoOvernemen(f.extraInformatie)}>Overnemen in de calculatie</button>
                  )}
                </div>
              )}
            </>
          )}
        </section>

        <section className="mb-4">
          <h3 className="text-base font-bold mb-1">Opleveringsbon</h3>
          <p className="text-sm">
            {!o.status ? 'Nog geen opleveringsbon in Bouwflow.' : (
              <>
                {OPLEVERINGSBON_LABEL[o.status as OpleveringsbonStatus] ?? o.status}
                {o.getekendOp ? `, door de klant ondertekend op ${fmtDate(o.getekendOp)}` : o.datum ? ` van ${fmtDate(o.datum)}, nog niet ondertekend door de klant` : ''}.
                {o.nieuw && <strong className="block text-accent-700 mt-1">Nieuw sinds de calculatie: kijk de posten die open stonden voor oplevering na.</strong>}
              </>
            )}
          </p>
        </section>

        {controle.extraOffertes.length > 0 && (
          <section className="mb-4">
            <h3 className="text-base font-bold mb-1">Nieuwe offertes na de opstartfiche</h3>
            <ul className="text-sm space-y-1 mb-2">
              {controle.extraOffertes.map((e) => (
                <li key={e.offerte.offerteId}>
                  <strong>Offerte {e.offerte.nummer}</strong>{e.offerte.datum ? ` van ${fmtDate(e.offerte.datum)}` : ''}{e.offerte.bedrag != null ? `, ${fmtEUR(e.offerte.bedrag)} aan de klant` : ''}: {e.lijnen.length} post{e.lijnen.length === 1 ? '' : 'en'}
                </li>
              ))}
            </ul>
            <button type="button" className="btn-primary btn-sm" disabled={bezig} onClick={() => onOffertesToevoegen(controle.extraOffertes)}>
              {bezig ? 'Toevoegen…' : 'Toevoegen ter bevestiging'}
            </button>
          </section>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <button type="button" className="btn-primary" onClick={onSluit}>Sluiten</button>
        </div>
      </div>
    </div>
  );
}
